import { Injectable } from '@angular/core';
import { SwUpdate, VersionReadyEvent } from '@angular/service-worker';
import { filter } from 'rxjs';

/** How often to ask the browser to re-check for a new service worker version. */
const UPDATE_CHECK_INTERVAL_MS = 6 * 60 * 60 * 1000; // 6 hours

/**
 * Watches for Angular Service Worker updates and reloads the page as soon as
 * a new version has finished downloading, instead of leaving the tab stuck
 * on a stale cached build until it is manually closed and reopened.
 *
 * Angular SW downloads a new version in the background but never activates
 * it for an already-open tab on its own — without this, a tester who leaves
 * a tab open (or just hits F5 mid-download) can run stale JS indefinitely.
 *
 * Angular's SW ONLY checks for a new version once, ~30s after the app
 * stabilizes on load (the `registerWhenStable:30000` default strategy) — it
 * never re-checks on its own afterward. A tab left open across a deploy (or
 * one that was last loaded slightly before a deploy finished) never learns a
 * new version exists at all: no VERSION_READY event ever fires, so this
 * service's subscription below never triggers, and the "update available"
 * banner (VersionService, which polls the backend's /version endpoint
 * separately) can show a newer version with no way to actually get it short
 * of a manual hard refresh. checkForUpdate() is Angular's own documented
 * fix for this gap: https://angular.dev/ecosystem/service-workers/communications
 */
@Injectable({ providedIn: 'root' })
export class AppUpdateService {

  constructor(private readonly swUpdate: SwUpdate) {}

  init(): void {
    if (!this.swUpdate.isEnabled) return;

    this.swUpdate.versionUpdates
      .pipe(filter((evt): evt is VersionReadyEvent => evt.type === 'VERSION_READY'))
      .subscribe(() => {
        this.swUpdate.activateUpdate().then(() => document.location.reload());
      });

    // Re-check periodically so an already-open tab eventually detects a
    // deploy that finished after the SW's one-time startup check.
    setInterval(() => this.swUpdate.checkForUpdate(), UPDATE_CHECK_INTERVAL_MS);

    // Also re-check whenever the tab regains focus/visibility — catches the
    // common case of a tab left open overnight or in a background pane
    // during a deploy, without waiting for the next interval tick.
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        this.swUpdate.checkForUpdate();
      }
    });
  }
}
